/**
 * فیدِ خانه — قرینه‌ی apps/mobile/app/(tabs)/index.tsx.
 *
 * یک فهرست، نه دو سربرگ: دعوت‌هایی که گرفتم و سیگنال‌هایی که خودم ساختم توی
 * همون یک ستون قاطی می‌شن و `orderFeed` مرتبشون می‌کنه — «کارِ مونده» بالا،
 * بایگانی پایین، گذشته‌ی بی‌شرکت اصلاً نه. اون تابع در `packages/core` است،
 * یعنی ترتیبِ سایت و اپ از یک کد می‌آد و نمی‌تونن از هم فاصله بگیرن.
 */
import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { notificationRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { DIMMED, bucketOf, orderFeed } from '@/core/lib/feedOrder';
import { Card, Note } from '@/ui/kit';
import { IncomingSignalCard, MineSignalCard } from '@/features/signals/SignalCard';

export function HomePage() {
  useLocale();
  const { profile, loading } = useSession();
  const qc = useQueryClient();

  // رخدادها (جوابِ دیگران، قطعی شدن قرار) همین‌جا دیده می‌شن، پس رسیدن به
  // این‌جا یعنی دیدیشون — همون قاعده‌ی موبایل. درخواستِ دوستی جدا می‌مونه،
  // چون آن هنوز جواب می‌خواهد.
  useEffect(() => {
    if (!profile) return;
    notificationRepo.markSeen(profile.id, 'rest')
      .then(() => qc.invalidateQueries({ queryKey: ['unreadByCategory'] }))
      .catch(() => undefined);
  }, [profile?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const incoming = useQuery({
    queryKey: ['incoming'],
    queryFn: () => signalRepo.incoming(profile!.id),
    enabled: !!profile,
  });
  const mine = useQuery({
    queryKey: ['mine'],
    queryFn: () => signalRepo.mine(profile!.id),
    enabled: !!profile,
  });

  const merged = useMemo(
    () => orderFeed(incoming.data ?? [], mine.data ?? []),
    [incoming.data, mine.data],
  );

  if (loading) return <div className="feed"><p className="dim">…</p></div>;

  // ورود با گوگل حساب می‌سازه ولی پروفایل نه. صفحه‌ی ساختِ پروفایل برشِ
  // بعدیه؛ تا اون‌موقع دستِ‌کم بگیم چرا فید خالیه.
  if (!profile) {
    return (
      <div className="feed">
        <Card><Note tone="brand">{t('oneStepLeft')}</Note></Card>
      </div>
    );
  }

  const busy = incoming.isLoading || mine.isLoading;
  const failed = incoming.isError || mine.isError;

  return (
    <div className="feed">
      {/* همون کلیدِ نوارِ کناری — یه عنوانِ جدا یعنی دو تا اسم برای یه چیز. */}
      <h1 className="feed-title">{t('signals')}</h1>
      {/* «برنامه‌های من» (WHOSUP-135) — همین بالای فید، قرینه‌ی آیکونِ تقویمِ موبایل. */}
      <p><Link to="/plans">{t('myPlans')} ←</Link></p>

      {failed ? <Note tone="warn">{t('errorRetry')}</Note> : null}
      {busy ? <p className="dim">…</p> : null}

      {!busy && !failed && merged.length === 0
        ? <Card><p className="dim">{t('noIncoming')}</p></Card>
        : null}

      {merged.map((row) => (row.kind === 'mine'
        ? <MineSignalCard key={`m-${row.item.id}`} signal={row.item} />
        : (
          <IncomingSignalCard key={`i-${row.item.id}`} signal={row.item}
            dim={DIMMED.includes(bucketOf(row.item))} />
        )))}
    </div>
  );
}
